import { Injectable, OnDestroy } from "@angular/core";
import { NgbModal, NgbModalOptions, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { BehaviorSubject, lastValueFrom, Subscription, timeout, TimeoutError } from "rxjs";
import { ActorActionEnum, Is_ANY_Download_Action, Is_Sign_Action } from "src/app/constants/actor-action.enum";
import { DocumentTypeEnum } from "src/app/constants/document-type.enum";
import { DocumentActor } from "src/app/models/actors";
import { EditorActor } from "src/app/models/requestEditor";
import { Workflow, WorkflowStep1Placeholder, WorkflowStep3Placeholder } from "src/app/models/workflow";
import { ActorApiClient } from "src/app/services/api-clients/actor.api-client";
import { WorkflowService } from "src/app/services/workflow.service";
import { EmailPreviewDialogComponent, EmailPreviewModalOptions } from "./email-preview-dialog/email-preview-dialog.component";
import { LoggingService } from "src/app/services/logging.service";
import { PdfPreparationDialogForBuilder } from "../general/pdf-preparation-dialog/pdf-preparation-dialog.component";
import { PlatformHubService } from "src/app/services/platformhub.service";
import { AuthenticationService } from "src/app/services/authentication.service";
import { IdentityOrganisationsApiClient } from "src/app/services/api-clients/identity-organisations.api-client";
import { User } from "src/app/models/user";
import { TranslateService } from "@ngx-translate/core";
import { ToastService } from "src/app/services/toast.service";
import { DocumentApiClient } from "src/app/services/document.apiclient";
import { Router } from "@angular/router";
import { HttpErrorResponse } from "@angular/common/http";
import { BaseActorCreateClass } from "src/app/models/api/actor-create.model";
import { DialogAddActionComponent, dialogAddActionOptions } from "./dialog-add-action/dialog-add-action.component";
import { ChangeActionsInPersonSendGroupModel } from "src/app/models/api/create-sendgroup.model";
import { DoesNotExistsIn, SingleOrDefault } from "src/app/_helpers/ArrayExtensionMethodHelper.functions";
import { IdentityProviderConstants } from "src/app/constants/identity-provider.constants";
import { ModalOpenerService } from "src/app/modals/modal-opener.service";
import { BuilderSendgroupApiClient } from "src/app/services/api-clients/builder-sendgroup.api-client";
import { OrganisationKvkModel } from "src/app/models/organisation-kvk.model";
import { OrganisationSettingsApiClient } from "src/app/services/organisation-settings.apiclient";
import { StatusEnum } from "src/app/constants/status.enum";
import { BuilderRequestApiClient } from "src/app/services/api-clients/builder-request.api-client";
import { BuilderValidationSummaryModel } from "src/app/models/builder/builder-validation-summary.model";
import { BuilderSendRefusedModel } from "src/app/models/builder/builder-send-refused.model";
import { BuilderSendRefusalReasonEnum } from "src/app/constants/builder-send-refusal-reason.enum";
import { HttpResponseCodesEnum } from "src/app/constants/http-request-statusses.enum";
import { BuilderRequestModel } from "src/app/models/builder/builder-request.model";
import { BuilderSendGroupModel, BuilderUpdateSendGroupDeadlineDaysModel, BuilderUpdateSendGroupDeadlineModel, BuilderUpdateSendGroupNameModel } from "src/app/models/builder/builder-sendgroup.model";
import { BuilderDocumentModel } from "src/app/models/builder/builder-document.model";
import { BuilderSteps } from "src/app/constants/builder-steps.enum";
import { BuilderActorModel } from "src/app/models/builder/builder-actor.model";
import { EditSignActionModal, EditSignActionModalOptions } from "src/app/modals/edit-sign-action/edit-sign-action.modal";
import { Provider, SignatureProviderAvailability } from "src/app/models/signatureProvider";
import { SignatureProviderTypeEnum } from "src/app/constants/signature-provider-type.enum";
import { EngineService } from "src/app/services/engine.service";
import { LocalStorageService } from "src/app/services/local-storage.service";
import { TheNaviGator } from "src/app/services/the-navi-gator.service";
import { IdentitySettings } from "src/app/constants/settings.enum";
import { MatchContacts } from "src/app/directives/match-contacts.function";
import { SetNormalizedEmail } from "src/app/directives/set-normalized-email.function";
import { RequestBuilderActionsEnum } from "src/app/constants/RequestBuilderActions.enum";
import { RequestApiClient } from "src/app/services/api-clients/request.apiclient";

@Injectable({ providedIn: 'root' })
export class RequestBuilderService implements OnDestroy {
  private _request!: BuilderRequestModel;
  private _documents!: BuilderDocumentModel[];
  private _actors!: DocumentActor[];
  private _validationSummary: BuilderValidationSummaryModel = new BuilderValidationSummaryModel();
  private _persons!: DocumentActor[];
  private _sendgroups!: BuilderSendGroupModel[];
  private _requestGuid!: string;
  private _allowedSignatureProviders!: Provider[];
  private _allSignatureProviders!: Provider[];

  public request$: BehaviorSubject<BuilderRequestModel> = new BehaviorSubject<BuilderRequestModel>({} as BuilderRequestModel);
  public documents$: BehaviorSubject<BuilderDocumentModel[]> = new BehaviorSubject<BuilderDocumentModel[]>([] as BuilderDocumentModel[]);
  public persons$: BehaviorSubject<DocumentActor[]> = new BehaviorSubject<DocumentActor[]>([] as DocumentActor[]);
  public validationSummary$: BehaviorSubject<BuilderValidationSummaryModel> = new BehaviorSubject<BuilderValidationSummaryModel>(this._validationSummary);
  public sendgroups$: BehaviorSubject<BuilderSendGroupModel[]> = new BehaviorSubject<BuilderSendGroupModel[]>([] as BuilderSendGroupModel[]);
  public actors$: BehaviorSubject<DocumentActor[]> = new BehaviorSubject<DocumentActor[]>([] as DocumentActor[]);
  public currentActor$: BehaviorSubject<DocumentActor> = new BehaviorSubject<DocumentActor>({} as DocumentActor);
  public workflowSteps$: BehaviorSubject<BuilderSteps[]> = new BehaviorSubject<BuilderSteps[]>([] as BuilderSteps[]);
  public workflowData$: BehaviorSubject<Workflow> = new BehaviorSubject<Workflow>({} as Workflow);
  public isInFirstSendgroup$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public signatureProviders$: BehaviorSubject<Provider[]> = new BehaviorSubject<Provider[]>([] as Provider[]);


  public requestGuid$: BehaviorSubject<string> = new BehaviorSubject<string>({} as string);
  private requestSignalRSubscription: Subscription | null = null;
  private usersubscription: Subscription | null = null;
  private urlSubscription: Subscription | null = null;

  private workflowId: string = "";

  public tempNewBulkActions: BaseActorCreateClass[] = [];


  workflowData?: Workflow;
  signYourself: boolean = false;
  secureFileSending: boolean = false;
  placeholdersStep3: boolean = false;
  allowedSignatureProvidersByWorkflow: string[] = [];
  inviteEmailModalRef: NgbModalRef | null = null;
  /**
   * Whether when picking SignatureProviders ESeal is allowed
   * -Default false, only allowed for ondertekenverzoek/Pdf (21-07-2025)
   * -READS from workflow
   */
  get allowESealSignatureProvider(): boolean {
    return this.workflowData?.step3?.allowsESeal!;
  };
  get allowAlreadySigned(): boolean {
    return !this.workflowData?.step1?.forbidAlreadySigned;
  };
  allowedSignatureProvidersByPlaceholder: any[] = [];
  sent: boolean = false;
  currentTab: BuilderSteps = BuilderSteps.AddDocuments;
  isMobile: boolean = false;
  shownNotSupportedToast: boolean = false;
  /**
   * Resets the 'shownNotSupportedToast' value so it can show the toast again
   */
  public resetNotSupportedOnUpload() {
    this.shownNotSupportedToast = false;
  }

  /*
    This window that is openend when creating a request whree you are the first actor
    Needed as variable since the opening of the window needs special care.
  */
  windowToOpenForIfActorInFirstSendGroup: Window | null = null;

  /**
   * MS we wait for the SEND to answer.
   * Sending is a heavy backend action (validations, credits, invites), but when it takes longer than this
   * we stop waiting and tell the user it MIGHT still be processed (the server keeps going, we only stop listening)
   */
  private readonly sendRequestTimeoutInMs: number = 120000;
  /**
   * Whether a SEND is currently in flight.
   * Every send ACTIVATES the workflow, so a second (double clicked) send must never be fired
   */
  private isSendingRequest: boolean = false;
  /**
   * Whether a SEND is currently in flight, for disabling/spinning the send button
   */
  public isSendingRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public signYourselfWorkflow$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public currentTab$: BehaviorSubject<BuilderSteps> = new BehaviorSubject(this.currentTab);



  /**
   * the ALLOWED identity providers linked to the initialized Workflow
   * Can be Empty, which means all NORMAL Identity providers should be used
   */
  onlyAllowedIdentityProviders: string[] = [];

  /**
   * This stores Closed/Expanded Sendgroups for Step 4.
   * They are stored here so they are retained on refrshes :)
   */
  collapsedSendgroups: string[] = [];

  modalRef!: any;

  deadlineDays!: number;
  deadline!: Date;
  workflowSteps: BuilderSteps[] = [];
  organisationHasKVK: boolean = false;
  SBRNexusEnvironmentForOrganisation!: string;
  user!: User;
  translations: any = <any>{};
  cleverbaseAuthenticationSetting: boolean = false;
  cleverbaseIdentificationSetting: boolean = false;

  /** An Array of the REQUIRED placeholders of Step 1 - Documents */
  private get requiredDocumentPlaceholdersIds(): number[] {
    // create the Placeholder Arrays for some basic validation:
    return Array.from(new Set(this.workflowData?.step1?.placeholders?.filter(item => !item.optional).map(item => item.placeholderId)));
  };
  /** An Array of the SET placeholders of Step 1 - Documents */
  private get setDocumentPlaceholdersIds(): number[] {
    // set the placeholderIds that are already assigned, for step1-2 validation:
    // only CHECK required placeholders:
    if (this.requiredDocumentPlaceholdersIds) {
      return Array.from(new Set(this._request.documentInfo?.filter(item => item.workflowPlaceholderId && this.requiredDocumentPlaceholdersIds.includes(item.workflowPlaceholderId!)).map(item => item.workflowPlaceholderId!)));
    } else {
      return [];
    }
  };
  /** An Array of the REQUIRED placeholders of Step 2 - Persons */
  private get requiredPersonPlaceholdersIds(): number[] {
    // create the Placeholder Arrays for some basic validation:
    return Array.from(new Set(this.workflowData?.step2?.placeholders?.filter(item => !item.optional).map(item => item.placeholderId)));
  }
  /** An Array of the SET placeholders of Step 2 - Persons */
  private get setPersonPlaceholdersIds(): number[] {
    // only CHECK required placeholders:
    if (this.requiredPersonPlaceholdersIds) {
      return Array.from(new Set(this._request.dossierPersons?.filter(item => item.workflowPlaceholderId && this.requiredPersonPlaceholdersIds.includes(item.workflowPlaceholderId!)).map(item => item.workflowPlaceholderId!)));
    } else {
      return [];
    }
  };

  /**
   * Whether step 1, Documents, is valid.
   * - Required placeholders (if any) are set
   * - a Document has been added
   */
  get HasValidStep1(): boolean {
    return this.currentDocumentCount >= 1 &&
      !this.hasInvalidFilingData &&
      ((!this.requiredDocumentPlaceholdersIds || this.requiredDocumentPlaceholdersIds.length == 0) || this.requiredDocumentPlaceholdersIds.length == this.setDocumentPlaceholdersIds.length) &&
      (!this.hasPreviousSignatures || this.allowAlreadySigned);
  }
  /**
   * Whether step 1, Documents, is not yet valid.
   * - Required placeholders not set
   * - no Documents at all
   */
  get HasInvalidStep1(): boolean {
    return !this.HasValidStep1;
  }
  /** Counts the current Documents assigned to this request or Defaults (0) */
  private get currentDocumentCount(): number {
    return this._request && this._request.documentInfo ? this._request.documentInfo?.length : 0;
  };
  /** Counts the current Documents assigned to this request or Defaults (0) */
  private get hasPreviousSignatures(): boolean {
    return this._request && this._request.documentInfo ? this._request.documentInfo.some(x => x.containsPreviousSignatures) : false;
  };
  /** lazy false check for whether the Request has (XBRI) invalid faling data */
  private get hasInvalidFilingData(): boolean {
    return this._request && this._request.hasInvalidFilingData;
  };



  /**
   * Whether step 2, Persons, is valid.
   * - Required placeholders (if any) are set
   * - a Person has been added
   */
  get HasValidStep2(): boolean {
    return this.currentPersonCount >= 1 && ((!this.requiredPersonPlaceholdersIds || this.requiredPersonPlaceholdersIds.length == 0) || this.requiredPersonPlaceholdersIds.length == this.setPersonPlaceholdersIds.length);
  }
  /**
   * Whether step 2, Persons, is not yet valid.
   * - Required placeholders not set
   * - no Persons at all
   */
  get HasInvalidStep2(): boolean {
    return !this.HasValidStep2;
  }
  /** Counts the current Persons assigned to this request or Defaults (0) */
  get currentPersonCount(): number {
    return this._request && this._request.dossierPersons ? this._request.dossierPersons?.length : 0;
  };


  triedToAddMyself: boolean = false;

  /**
   *
   * @param navigate The navi Gator that replaces Router to send you to different pages/modules
   */
  constructor(private translateService: TranslateService,
    private toastService: ToastService,
    private workflowService: WorkflowService,
    private actorApiClient: ActorApiClient,
    private modalService: NgbModal,
    private loggingService: LoggingService,
    private platformHub: PlatformHubService,
    private authenticationService: AuthenticationService,
    private documentService: DocumentApiClient,
    private identityOrganisationService: IdentityOrganisationsApiClient,
    private router: Router,
    private sendGroupApiClient: BuilderSendgroupApiClient,
    private modalOpenerService: ModalOpenerService,
    private organisationSettingsApiClient: OrganisationSettingsApiClient,
    private requestBuilderApiClient: BuilderRequestApiClient,
    private requestApiClient: RequestApiClient,
    private engineService: EngineService,
    private localStorageService: LocalStorageService,
    private navigate: TheNaviGator
  ) {
    this.getSignatureProviders();
    this.getTranslations();
    translateService.onLangChange.subscribe(() => {
      this.getTranslations();
    });
    this.usersubscription = this.authenticationService.currentUser.subscribe((data) => {
      this.user = data;
    });
    this.requestSignalRSubscription = this.platformHub.builderRequest$.subscribe((request: BuilderRequestModel) => {
      if (request && request.id === this._requestGuid) {
        this.setRequest(request);
        if (request.workgroup && !request.workgroupName) {
          request.workgroupName = this.authenticationService.getWorkGroupName(request.workgroup);
        }
        if (request.documentInfo) {
          this.CheckDocuments().catch((error) => this.loggingService.logException(error));
        }
      }
    });
    this.urlSubscription = this.router.events
      .subscribe(() => {
        const currentUrl = this.router.url;
        if (this._requestGuid.trim().length === 0 || !currentUrl.includes(this._requestGuid)) {
          this.setRequest({} as BuilderRequestModel);
        }

        // Je kunt hier ook opnieuw de routeparam uitlezen:
        const buildId = currentUrl.split('/')[2]; // of beter via ActivatedRoute (zie hieronder)
      });
    this.isMobile = /(iPhone|iPod|iPad|Android|webOS|BlackBerry|IEMobile|Opera Mini)/.exec(navigator.userAgent) !== null;
    this.getSignatureProviders();
  }

  ngOnDestroy(): void {
    if (this.usersubscription) {
      this.usersubscription.unsubscribe();
    }
    if (this.requestSignalRSubscription) {
      this.requestSignalRSubscription.unsubscribe();
    }
    this.triedToAddMyself = false;
  }

  getTranslations() {
    this.translateService.get([
      'Toasts.ToastsRequestDocumentDeleted',
      'Toasts.UpdateDossierError',
      'Toasts.GetDocumentError',
      'Toasts.RequestSentError',
      'Toasts.RequestSendValidationsFailed',
      'Toasts.RequestSendProcessing',
      'Toasts.RequestSendAlreadySent',
      'Toasts.RequestSendNotReady',
      'Toasts.RequestSendNoAccess',
      'Toasts.RequestSendNoConnection',
      'Toasts.RequestSendTakingLong',
      'Toasts.RequestSendUnexpectedError',
      'Toasts.RequestsPurgeError',
      'Toasts.DocumentTypeNotSupportedInWorkflow',
      'Toasts.NoChamberOfCommerceNumberError',
      'Toasts.Error',
      'Toasts.Warning',
      'Toasts.Sent',
      'Toasts.RequestSent',
      'Toasts.Deleted',
      'Toasts.Saved',
      'Toasts.RequestDocumentDeleteError',
      'Toasts.AddPersonError',
      'Toasts.RenderingIsntReady',
      'Toasts.UseOneOfTheFollowing',
      'Toasts.Purged',
      'Toasts.RequestPurged',
      'Request.DropFilesOr'
    ]).subscribe(translation => {
      this.translations.toastsRequestDocumentDeleted = translation['Toasts.ToastsRequestDocumentDeleted'];
      this.translations.toastsUpdateDossierError = translation['Toasts.UpdateDossierError'];
      this.translations.toastsGetDocumentError = translation['Toasts.GetDocumentError'];
      this.translations.toastsRequestSentError = translation['Toasts.RequestSentError'];
      this.translations.toastsRequestSendValidationsFailed = translation['Toasts.RequestSendValidationsFailed'];
      this.translations.toastsRequestSendProcessing = translation['Toasts.RequestSendProcessing'];
      this.translations.toastsRequestSendAlreadySent = translation['Toasts.RequestSendAlreadySent'];
      this.translations.toastsRequestSendNotReady = translation['Toasts.RequestSendNotReady'];
      this.translations.toastsRequestSendNoAccess = translation['Toasts.RequestSendNoAccess'];
      this.translations.toastsRequestSendNoConnection = translation['Toasts.RequestSendNoConnection'];
      this.translations.toastsRequestSendTakingLong = translation['Toasts.RequestSendTakingLong'];
      this.translations.toastsRequestSendUnexpectedError = translation['Toasts.RequestSendUnexpectedError'];
      this.translations.toastsRequestsPurgeError = translation['Toasts.RequestsPurgeError'];
      this.translations.toastsDocumentTypeNotSupportedInWorkflow = translation['Toasts.DocumentTypeNotSupportedInWorkflow'];
      this.translations.toastsNoChamberOfCommerceNumberError = translation['Toasts.NoChamberOfCommerceNumberError'];
      this.translations.error = translation['Toasts.Error'];
      this.translations.warning = translation['Toasts.Warning'];
      this.translations.toastsSent = translation['Toasts.Sent'];
      this.translations.toastsRequestSent = translation['Toasts.RequestSent'];
      this.translations.toastsDeleted = translation['Toasts.Deleted'];
      this.translations.toastsSaved = translation['Toasts.Saved'];
      this.translations.toastsRequestDocumentDeleteError = translation['Toasts.RequestDocumentDeleteError'];
      this.translations.toastsAddPersonError = translation['Toasts.AddPersonError'];
      this.translations.toastsRenderingIsntReady = translation['Toasts.RenderingIsntReady'];
      this.translations.toastsUseOneOfTheFollowing = translation['Toasts.UseOneOfTheFollowing'];
      this.translations.toastsPurged = translation['Toasts.Purged'];
      this.translations.toastsRequestPurged = translation['Toasts.RequestPurged'];
      this.translations.or = translation['Request.DropFilesOr'];
    });
  }



  /**
   *
   * @param workflowId Loads the workflow associated with the Id into this service for Use
   */
  async initializeForWorkflow(workflowId: string) {
    this.workflowData = await this.workflowService.getWorkflowById(workflowId)
    this.onlyAllowedIdentityProviders = this.workflowData.step2.allowedIdentityProviders;

    this.checkWorkflows();
  }

  public changeCurrentTab(tabName: BuilderSteps) {
    this.currentTab = tabName;
    // if()
    this.currentTab$.next(this.currentTab);

    this.checkWorkflows();
  }

  private async checkWorkflows() {
    this.signYourself = (this.workflowData?.step2?.skipStep && this.workflowData?.step4?.skipStep) ?? false;
    this.signYourselfWorkflow$.next(this.signYourself);
    this.secureFileSending = this.workflowData?.step3?.skipStep ?? false;
    this.placeholdersStep3 = (this.workflowData?.step3?.placeholders && this.workflowData?.step3?.placeholders.length > 0) ?? false;
    this.allowedSignatureProvidersByWorkflow = this.workflowData?.step3.allowedQtsps ?? [];
    // also verify providers on a workflow base:
    this.verifyAllowedSignatureProvidersForWorkflow();

    if (this.placeholdersStep3) {
      this.workflowData!.step3.placeholders.forEach(placeholder => {
        if (this.allowedSignatureProvidersByPlaceholder.length === 0 || this.allowedSignatureProvidersByPlaceholder.some(s => s.placeholderId == placeholder.placeholderId)) {
          this.allowedSignatureProvidersByPlaceholder.push({ placeholderId: placeholder.placeholderId, allowedQTSP: placeholder.allowedQtsps });
        }
      });
    }


    if (this.signYourself && this.currentTab === BuilderSteps.AddActions) {
      await this.addActionsSignYourselfWorkflow(this._documents);
    }
    if (this.placeholdersStep3 && this.currentTab === BuilderSteps.AddActions) {
      await this.addPlaceholderActions(this.workflowData?.step3?.placeholders ?? []);
    }
    if (this.secureFileSending && this.currentTab === BuilderSteps.ReviewAndSend) {
      await this.addSecureFileSendingActions(this._persons);
    }
  }

  public getRequestSendGroups(): BuilderSendGroupModel[] {
    return this._request.builderSendgroups!;
  }

  public getRequestUniqueActors() {
    return this._actors.reduce((unique: any[], o: any) => {
      if (!unique.some((obj: EditorActor) => obj.id === o.id)) {
        unique.push(o);
      }
      return unique;
    }, []);
  }

  public getCountActionsOfActor(actorId: string) {
    return this._actors.filter(actor => actor.dossierPersonId === actorId).length ?? 0;
  }

  public getCountActorsOfDocument(documentId: string) {
    return this._actors.filter(actor => actor.documentId === documentId).length ?? 0;
  }

  public getActionsPerDocument(documentId: string) {
    return this._actors.filter(actor => actor.documentId === documentId);
  }

  async openEmailPreview(event: any) {
    let actor = this._actors.filter(item => item.dossierPersonId === event.id && item.action)[0];
    if (actor && actor !== undefined) {
      (await this.actorApiClient.getEmailTemplate(this._request.id, actor.id)).subscribe({
        next: (value: any) => {
          if (this.inviteEmailModalRef) return;
          this.inviteEmailModalRef = this.modalService.open(EmailPreviewDialogComponent, EmailPreviewModalOptions);
          this.inviteEmailModalRef.componentInstance.emailTemplate = value;
          this.inviteEmailModalRef.closed.subscribe(() => {
            this.inviteEmailModalRef = null;
          })
        }
      });
    }
  }

  public openActionDialog(documentGuid: string, action: ActorActionEnum) {
    // SHORTCUT if only 1 Person:
    if (this._persons && this._persons.length == 1) {
      let personId = this._persons[0].dossierPersonId;
      let selectedQtspsAsStringArray: string[] = this.workflowData?.step3?.allowedQtsps ?? [];
      // this._availableSignatureProviders.selectedSignatureProviders.forEach(qtsp => {
      //   selectedQtspsAsStringArray.push(this.getProviderValue(qtsp));
      // });
      this.AddNewBulkActorForRequest(action, personId, documentGuid, null, selectedQtspsAsStringArray);
      this.SendBulkActorsRequest(action);
      return;
    }


    this.modalRef = this.modalService.open(DialogAddActionComponent, dialogAddActionOptions);
    this.modalRef.componentInstance.action = action;
    this.modalRef.componentInstance.request = this._request;
    let y = this;
    this.modalRef.componentInstance.newAction.subscribe(async (dataModel: any) => {
      this.modalRef.close();

      dataModel.personIds.forEach(async (personId: string) => {
        let selectedQtspsAsStringArray: string[] = this.workflowData?.step3?.allowedQtsps ?? [];
        // this._availableSignatureProviders.selectedSignatureProviders.forEach(qtsp => {
        //   selectedQtspsAsStringArray.push(this.getProviderValue(qtsp));
        // });
        this.AddNewBulkActorForRequest(action, personId, documentGuid, null, selectedQtspsAsStringArray);
      });
      this.SendBulkActorsRequest(action);


    });
  }

  public openActionDialogWithoutDocumentId(action: ActorActionEnum) {
    this.openActionDialog(this._documents[0].id, action);
  }

  public setRequestGuid(requestGuid: string) {
    let previousRequestGuid = this._requestGuid;
    this._requestGuid = requestGuid;
    if (previousRequestGuid == this._requestGuid) {
      // no changes
      return;
    }

    this.requestGuid$.next(this._requestGuid);

    if (this._requestGuid === "") {
      // we are resetting requestGuid;
      this.setRequest({} as BuilderRequestModel);
      // also reset previous validationSummary:
      this._validationSummary = new BuilderValidationSummaryModel();
      this.validationSummary$.next(this._validationSummary);
      return;
    }
  }
  public setRequest(request: BuilderRequestModel) {
    this._request = request;
    if (!this._request || this._request && Object.keys(this._request).length === 0) {
      this.setRequestGuid("");
      this.setDocuments([]);
      this.setPersons([]);
      this.setActors([]);
      this.setSendgroups([]);
      this.setAllowedSignatureProvidersByPlaceholder([]);
      this.setAllowedSignatureProvidersByWorkflow([]);

      this.request$.next(this._request);
      return;
    }
    if (this._request.documentInfo?.length > 0) {
      this._request.documentInfo = this._request.documentInfo.sort((a: BuilderDocumentModel, b: BuilderDocumentModel) => {
        return a.dossierIndex - b.dossierIndex;
      });
    }

    this.request$.next(this._request);


    // It is either SET, or reSET :)
    if (this._request.documentInfo) { this.setDocuments(this._request.documentInfo); } else { this.setDocuments([]); }
    if (this._request.dossierPersons) { this.setPersons(this._request.dossierPersons); } else { this.setPersons([]); }
    if (this._request.actors) { this.setActors(this._request.actors); } else { this.setActors([]); }
    if (this._request.builderSendgroups) { this.setSendgroups(this._request.builderSendgroups); } else { this.setSendgroups([]); }

    // add yourself when getting request + workflow info
    // this means YOU are added when going to the action/send step
    if (this.signYourself && this._requestGuid && this._persons?.length <= 0 && this._actors?.length <= 0 && !this.triedToAddMyself) {
      this.triedToAddMyself = true;
      this.addMyselfForSigning();
    }

  }

  public OpenXMLView(event: any) {
    this.modalOpenerService.ViewXml(this._requestGuid, event.document);
  }

  public OpenXBRLViewer(event: any) {
    this.modalOpenerService.ViewXblrOrTxt(this._request, event.document, event.goToError);
  }

  public OpenDocumentDetails(event: any) {
    this.modalOpenerService.DocumentDetails(event.document);
  }


  async OpenPDFViewer(event: any) {
    let ngbModalOptions: NgbModalOptions;
    if (!this.isMobile) {
      ngbModalOptions = {
        backdrop: 'static',
        keyboard: false,
        centered: false,
        modalDialogClass: 'pkiMd',
        windowClass: 'addSignatureFieldModal'
      };
    }
    else {
      ngbModalOptions = {
        backdrop: 'static',
        keyboard: false,
        centered: false,
        size: 'xl',
        windowClass: 'addSignatureFieldModalMobile'
      };
    }

    this.modalRef = this.modalService.open(PdfPreparationDialogForBuilder, ngbModalOptions);
    let modal: PdfPreparationDialogForBuilder = this.modalRef.componentInstance;
    modal.disableSignatureProviders = (this.allowedSignatureProvidersByPlaceholder.some(s => s.allowedQTSP) || this.allowedSignatureProvidersByWorkflow.length > 0);

    //TODO: refactor to proper inputs and events
    let doc = this._documents.find(d => d.id === event.documentGuid);
    modal.document = doc!;
    // modal.request = this._request;
    modal.actorGuid = event.actor;
    //modal.isMobile = this.isMobile!;
    modal.place = event.place ?? true;
    modal.displayfields = event.displayfields ?? true;
    modal.setMoreSignaturefields = event.actors && event.actors.length > 0;
    modal.allowedMultipleSignaturefields = event.allowedMultipleSignaturefields ?? true;
    modal.showEditDocument = event.showEditDocument ?? false;


    if (!this.isMobile) {
      modal.zoomLevelChanged.subscribe(async (item: any) => {
        this.modalRef._windowCmptRef.instance.modalDialogClass = item.className;
        setTimeout(() => modal.afterZoomLevelChanged(), 100);
      });
    }
    modal.signatureFieldChanged.subscribe(async (sigField: any) => {
      modal.close();

      await this.updateActionPerson(sigField);
      if (event.actors && event.actors.length > 0) {
        if (sigField.newActorGuid) {
          modal.actorGuid = sigField.newActorGuid;
          this.OpenPDFViewer({ documentGuid: event.documentGuid, actor: sigField.newActorGuid, actors: event.actors });
        } else {
          let actor = event.actors[0];
          event.actors.splice(0, 1);
          let existingModal = this.modalRef?._windowCmptRef?.location?.nativeElement;
          if (existingModal) existingModal.parentNode?.removeChild(existingModal);
          modal.actorGuid = actor;
          this.OpenPDFViewer({ documentGuid: event.documentGuid, actor: actor, actors: event.actors });
        }
      } else if (sigField.newActorGuid) {
        modal.actorGuid = sigField.newActorGuid;
        this.OpenPDFViewer({ documentGuid: event.documentGuid, actor: sigField.newActorGuid });
      }
    });
  }

  async OpenEditSignAction(actor: BuilderActorModel) {
    this.modalRef = this.modalService.open(EditSignActionModal, EditSignActionModalOptions);

    let modal = this.modalRef.componentInstance as EditSignActionModal;
    modal.saveActor.subscribe(async (savedActor: BuilderActorModel) => {
      await this.updateActionPerson({ documentId: actor?.documentId, actor: savedActor });
      actor = savedActor;
      modal.close();
      this.currentActor$.next({} as DocumentActor);
    })
  }


  public DeleteActorFromRequest(inviteId: string) {
    this.actorApiClient.Delete(this._requestGuid, inviteId).subscribe(() => {
      // trigger validation
      this.Validate();
    });
  }

  public EditSignAction(actor: BuilderActorModel) {
    this.currentActor$.next(actor);
    this.OpenEditSignAction(actor);
  }

  public CreateSingularActorForRequest(action: ActorActionEnum, personGuid: string, documentGuid: string) {
    this.actorApiClient.CreateSingular(this._requestGuid, action, personGuid, documentGuid).subscribe();
    // THIS REFRESHES IN BE
  }
  /**
   * YOU CAN ASSIGN NEW ACTIONS FOR THE BULK SENDING
   * ALWAYS USE:  SendBulkActorsRequest to finalize this call
   * @param action
   * @param personGuid
   * @param documentGuid
   * @param workflowPlaceholderId OPTIONAL, whether this action is linked to a workflow placeholder
   * @param allowedQtsps OPTIONAL, allowed signing method Qtsps, this is used to filter
   * @param identityProvider OPTIONAL, IdentityProvider that is required for login (used to force cleverbase)
   */
  public AddNewBulkActorForRequest(action: ActorActionEnum, personGuid: string, documentGuid: string, workflowPlaceholderId: number | null = null, allowedQtsps: string[] | null = null, identityProvider: string | null = null) {
    if (!this.tempNewBulkActions) {
      this.tempNewBulkActions = [];
    }
    var newActionModel = new BaseActorCreateClass();
    newActionModel.action = action;
    newActionModel.personGuid = personGuid;
    newActionModel.documentGuid = documentGuid;
    newActionModel.workflowPlaceholderId = workflowPlaceholderId;
    newActionModel.allowedQtsps = allowedQtsps;
    newActionModel.identityProvider = identityProvider;
    this.tempNewBulkActions.push(newActionModel);
  }
  /**
   * SENDS all setup BULK actions
   * ALWAYS USE:  AddNewbulkActorForRequest to Setup this call
   * @param action
   * @param personGuid
   * @param documentGuid
   */
  public SendBulkActorsRequest(action: string | null = null) {
    if (!this.tempNewBulkActions || this.tempNewBulkActions.length === 0) {
      return;
    }
    this.actorApiClient.CreateBulk(this._requestGuid, this.tempNewBulkActions).subscribe({
      next: (data: any) => {
        data.forEach((d: any) => {
          let actorIndex = this._actors.findIndex((s: DocumentActor) => s.id === d.inviteId);
          if (actorIndex > -1 && this._actors[actorIndex].action === ActorActionEnum.Sign) {
            this._actors[actorIndex].isCustomer = d.isCustomer;

            this._actors[actorIndex].preferredCscProvider = d.preferredCscProvider;
            this._actors[actorIndex].preferredSignatureProvider = d.preferredSignatureProvider;
            this.getAvailableSignatureProviders(this._actors[actorIndex].id);
          }
        });
        if (action === ActorActionEnum.Sign) {
          let actorInviteIds = data.filter((actor: any) => actor.inviteId != data[0].inviteId);
          var matchedDocument = SingleOrDefault(this._documents, "id", this.tempNewBulkActions[0].documentGuid);
          if (matchedDocument && matchedDocument.documentType == DocumentTypeEnum.RegularPdf && matchedDocument.documentStatus != StatusEnum.Processing) {
            this.OpenPDFViewer({ documentGuid: this.tempNewBulkActions[0].documentGuid, actor: data[0].inviteId, actors: actorInviteIds.map((d: any) => d.inviteId) });
          }
        }
        this.tempNewBulkActions = [];
        if (this.secureFileSending && this.currentTab === "ReviewAndSend") {
          this.mergeSendgroups();
        }

        // trigger validation
        this.Validate();
      }, error: () => {
        this.tempNewBulkActions = [];
      }
    });

  }

  public async updateActionPerson(event: any) {
    if (!event.actor || !event.actor.id) return;
    try {
      let updateActionPerson = await this.actorApiClient.updateActionToPerson(this._requestGuid!, event.documentId, event.actor);
      let value: any = await lastValueFrom(updateActionPerson);

      let actorIndex = this._actors?.findIndex(item => item.id == value.id);
      if (this.workflowData?.step3.placeholders && actorIndex && actorIndex === -1) {
        this._actors[actorIndex] = value;
        event.actor = value;
      } else if (actorIndex && actorIndex > -1) {
        this._actors[actorIndex] = value;
        event.actor = value;
      }
      if (this.signYourself && this._documents.length === 1 && ((event.actor.sigFieldW === 0 && event.actor.sigFieldH === 0 && event.actor.sigFieldX === 0 && event.actor.sigFieldY === 0) || (event.actor.sigFieldW === undefined && event.actor.sigFieldH === undefined && event.actor.sigFieldX === undefined && event.actor.sigFieldY === undefined))) {
        this.OpenPDFViewer({ documentGuid: event.documentGuid, actor: event.actor.id });
      }

      // trigger validation
      this.Validate();
    } catch (error: any) {
      this.loggingService.logException(error);
    }
  }

  private async addPlaceholderActions(placeholders: WorkflowStep3Placeholder[]) {
    if (this._actors.length > 0 &&
      // each type has atleast 1 action
      placeholders.every(ph => this._actors.map(s => s.workflowPlaceholderId).includes(ph.placeholderId ?? 0))
      &&
      // all placeholders with default actions:
      placeholders.filter(x => x.defaultActions.length > 0)
        // for every placeholder with default actions:
        .every(ph =>
          // for every default action:
          ph.defaultActions.every(defaultAction =>
            // has it been added for a workflowplaceholder:
            this._actors.filter(x => x.workflowPlaceholderId).map(x => x.action).includes(defaultAction)
          ))
    ) {
      return;
    }
    if (placeholders.length <= 0) {
      let signActorsWithoutFields = [...this._actors.filter(s => Is_Sign_Action(s.action) && s.sigFieldPage < 0)];
      if (signActorsWithoutFields.length > 0 && this._documents.find(doc => doc.id === signActorsWithoutFields[0].documentId)?.documentType === DocumentTypeEnum.RegularPdf) {
        signActorsWithoutFields.splice(signActorsWithoutFields.findIndex(s => s.actorId === signActorsWithoutFields[0].actorId), 1);
        this.OpenPDFViewer({ documentGuid: this._actors.filter(s => Is_Sign_Action(s.action))[0].documentId, actor: this._actors.filter(s => Is_Sign_Action(s.action))[0].id, allowedMultipleSignaturefields: true });
      }
      return;
    }

    if (!placeholders || placeholders.length <= 0) {
      return;
    }
    placeholders.forEach(placeholder => {
      if ((placeholder.defaultPersonPlaceholderId === null || placeholder.defaultPersonPlaceholderId === undefined) &&
        (placeholder.defaultDocumentPlaceholderId === null || placeholder.defaultDocumentPlaceholderId === undefined) &&
        (placeholder.defaultActions === null || placeholder.defaultActions === undefined || placeholder.defaultActions.length == 0)) {
        return;
      }
      // ARRANGE document for creation
      let placeholderDocumentId = this._documents.find(s => s.workflowPlaceholderId === placeholder.defaultDocumentPlaceholderId)?.id;
      // some actions have no DOCUMENTid, but its still required
      if ((placeholderDocumentId === null || placeholderDocumentId === undefined)) {
        return;
      }
      // Arrange personId(S) for creation
      let placeholderPersons: DocumentActor[] = this._persons.filter(s => s.workflowPlaceholderId === placeholder.defaultPersonPlaceholderId)!;
      if (placeholderPersons.length === 0 && placeholder.alternativePersonPlaceholderId) {
        placeholderPersons = this._persons.filter(s => s.workflowPlaceholderId === placeholder.alternativePersonPlaceholderId)!;
      }
      // now for the persons in placeholders:
      for (const dossierPerson of placeholderPersons) {

        // we are going to assign EVERY default action instead of 1
        for (const defaultAction of placeholder.defaultActions) {
          let actorExists = this._actors.findIndex(actor => actor.workflowPlaceholderId === placeholder.placeholderId && actor.action === defaultAction) > -1;
          if (!actorExists) {
            this.AddNewBulkActorForRequest(defaultAction as ActorActionEnum, dossierPerson.dossierPersonId, placeholderDocumentId!, placeholder.placeholderId, placeholder.allowedQtsps, dossierPerson.identityProvider);
          }
        }
      }
    });
    if (placeholders.length > 1) {
      this.SendBulkActorsRequest();
    } else {
      const firstPlaceholder = placeholders[0];
      const defaultAction = firstPlaceholder.defaultActions[0];;
      this.SendBulkActorsRequest(defaultAction);
    }

  }

  private async addActionsSignYourselfWorkflow(documents: BuilderDocumentModel[], i: number = 0) {
    let tempDocumentList = documents;
    if (!tempDocumentList || tempDocumentList.length < 1) {
      return;
    }
    if (this._actors.length > 0) {
      return;
    }
    if (this._persons.length > 0) {
      tempDocumentList.forEach(document => {
        let selectedQtspsAsStringArray: string[] = this.workflowData?.step3?.allowedQtsps ?? [];
        // this._availableSignatureProviders.selectedSignatureProviders.forEach(qtsp => {
        //   selectedQtspsAsStringArray.push(this.getProviderValue(qtsp));
        // });
        this.AddNewBulkActorForRequest(ActorActionEnum.Sign, this._persons[0].dossierPersonId, document.id, null, selectedQtspsAsStringArray);
      });
      this.SendBulkActorsRequest(ActorActionEnum.Sign);
    }
  }

  private async addSecureFileSendingActions(persons: DocumentActor[]) {
    if (!persons || persons.length < 1) {
      return;
    }
    let personsWithoutActions: DocumentActor[] = persons.filter(s => DoesNotExistsIn(this._actors, "dossierPersonId", s.dossierPersonId));

    if (personsWithoutActions && personsWithoutActions.length > 0) {
      personsWithoutActions.forEach(person => {
        this.AddNewBulkActorForRequest(ActorActionEnum.Download, person.dossierPersonId, this._documents[0].id);
      });
      this.SendBulkActorsRequest();
    }
  }

  public setPersons(persons: DocumentActor[] = []) {
    this._persons = persons;
    this.persons$.next(this._persons);
  }

  public setActors(actors: DocumentActor[] = []) {
    this._actors = actors;
    this._actors.forEach(actor => {
      this.getAvailableSignatureProviders(actor.id);
    })
    this.actors$.next(this._actors);
  }

  public setValidationSummary(validationSummary: BuilderValidationSummaryModel | undefined | null) {
    if (!validationSummary) {
      this._validationSummary = new BuilderValidationSummaryModel();
      this.validationSummary$.next(this._validationSummary);

      return;
    }
    this._validationSummary = validationSummary;
    this.validationSummary$.next(this._validationSummary);
  }

  public setSendgroups(sendgroups: BuilderSendGroupModel[] | undefined | null) {
    if (!sendgroups) {
      return;
    }

    //ALWAYS order the grouped actions for a person based the persons tab.
    sendgroups.forEach(sendgroup => {
      let newActionOrderForSendgroups: EditorActor[] = [];
      this._persons = this._persons.sort((personA: DocumentActor, personB: DocumentActor) => personA.ordinal - personB.ordinal);
      this._persons.forEach(person => {
        let sendgroupActions = sendgroup.actions.filter(action => action.personGuid === person.dossierPersonId);
        newActionOrderForSendgroups = newActionOrderForSendgroups.concat(sendgroupActions);
      });
      sendgroup.actions = newActionOrderForSendgroups;
    });

    this._sendgroups = sendgroups;
    this.sendgroups$.next(this._sendgroups);
    if (this._sendgroups?.length > 0 && this._actors?.length > 0) {
      let isCurrentUserAnActorIndex = this._actors.findIndex(actor => MatchContacts(this.user, actor));
      if (isCurrentUserAnActorIndex > -1) {
        let isCurrentUserAnActorAsDossierPersonId = this._actors[isCurrentUserAnActorIndex].dossierPersonId;
        if (this._sendgroups[0].actions.filter(s => s.personGuid === isCurrentUserAnActorAsDossierPersonId).length > 0) {
          this.isInFirstSendgroup$.next(true);
        } else {
          this.isInFirstSendgroup$.next(false);
        }
      } else {
        this.isInFirstSendgroup$.next(false);
      }
    }
  }

  private setAllowedSignatureProvidersByPlaceholder(allowedQTSPs: string[]) {
    this.allowedSignatureProvidersByPlaceholder = allowedQTSPs;
  }

  private setAllowedSignatureProvidersByWorkflow(allowedQTSPs: string[]) {
    this.allowedSignatureProvidersByWorkflow = allowedQTSPs;
  }

  public updateSendgroupName(sendGroupGuid: string, sendGroupName: string) {
    var updateModel = new BuilderUpdateSendGroupNameModel();
    updateModel.sendGroupGuid = sendGroupGuid;
    updateModel.sendGroupName = sendGroupName;
    this.sendGroupApiClient.updateSendGroupName(this._requestGuid, updateModel).subscribe();
  }

  public updateSendgroupDeadline(sendGroupGuid: string, sendgroupDeadline: Date) {
    var updateModel = new BuilderUpdateSendGroupDeadlineModel();
    updateModel.sendGroupGuid = sendGroupGuid;
    updateModel.deadline = sendgroupDeadline;

    this.sendGroupApiClient.updateSendGroupDeadline(this._requestGuid, updateModel).subscribe();
  }

  public updateSendgroupDeadlineDays(sendGroupGuid: string, sendGroupDeadlineDays: number) {
    var updateModel = new BuilderUpdateSendGroupDeadlineDaysModel();
    updateModel.sendGroupGuid = sendGroupGuid;
    updateModel.deadlineDays = sendGroupDeadlineDays;

    this.sendGroupApiClient.updateSendGroupDeadlineDays(this._requestGuid, updateModel).subscribe();
  }

  public setDocuments(documents: BuilderDocumentModel[] = []) {
    this._documents = documents;
    this.documents$.next(this._documents);
  }

  public setCurrentTab(currentTab: BuilderSteps = BuilderSteps.AddDocuments) {
    this.currentTab = currentTab;
    this.currentTab$.next(this.currentTab);
  }

  public setWorkflowId(workflowId: string) {
    this.workflowId = workflowId;
  }

  public getWorkflowId() {
    return this.workflowId;
  }

  async setWorkflow() {
    this.workflowSteps = [BuilderSteps.AddDocuments, BuilderSteps.AddPersons, BuilderSteps.AddActions, BuilderSteps.ReviewAndSend];
    if (!this.workflowId) {
      // somehow you tried to open the builder without 'instructions' (workflow)
      // make sure instructions are loaded:
      let defaultWorkflowGuid = this.workflowService.getDefaultWorkflowGuid();
      this.workflowId = defaultWorkflowGuid;
    }
    // ALWAYS load a workflow:
    try {
      let value: Workflow = await this.workflowService.getWorkflowById(this.workflowId);
      // also load the workflow data into the service so it can be used anywhere in the page!
      this.initializeForWorkflow(this.workflowId);

      this.workflowData = value;
      if (this.workflowData?.step1?.skipStep) this.workflowSteps.splice(this.workflowSteps.indexOf(BuilderSteps.AddDocuments), 1);
      if (this.workflowData?.step2?.skipStep) this.workflowSteps.splice(this.workflowSteps.indexOf(BuilderSteps.AddPersons), 1);
      if (this.workflowData?.step3?.skipStep) this.workflowSteps.splice(this.workflowSteps.indexOf(BuilderSteps.AddActions), 1);
      if (this.workflowData?.step4?.skipStep) this.workflowSteps.splice(this.workflowSteps.indexOf(BuilderSteps.ReviewAndSend), 1);

      this.checkSettings();

      this.workflowData$.next(this.workflowData);
      this.workflowSteps$.next(this.workflowSteps);

    } catch (error: any) {
      this.loggingService.logException(error);
    }
    // this.activeTab = this.workflowSteps[0];
    this.signYourself = (this.workflowData?.step2?.skipStep && this.workflowData?.step4?.skipStep) ?? false;

  }

  private async checkSettings() {
    (await this.identityOrganisationService.getOrganisationClaim(this.user.organisationGuid, IdentitySettings.CleverbaseAuthentication)).subscribe((authenticationData: any) => {
      this.cleverbaseAuthenticationSetting = authenticationData != null && (authenticationData.value === null || authenticationData.value?.toLowerCase() == "true");
    });
    (await this.identityOrganisationService.getOrganisationClaim(this.user.organisationGuid, IdentitySettings.CleverbaseIdentification)).subscribe((identificationData: any) => {
      this.cleverbaseIdentificationSetting = identificationData != null && (identificationData.value === null || identificationData.value?.toLowerCase() == "true");
    });
    if (this.workflowData?.requireKVK) {
      this.identityOrganisationService.getOrganisationKvk(this.user.organisationGuid).subscribe((kvkModel: OrganisationKvkModel) => {
        this.organisationHasKVK = kvkModel != null && kvkModel != undefined && kvkModel.kvk != null && kvkModel.kvk != undefined && kvkModel.kvk != "";
      });
      this.organisationSettingsApiClient.getOrganisationSettings(this.user.organisationGuid).subscribe((data) => {
        let index = data.findIndex(os => os.key == "SBRNexusEnvironment")
        if (index > -1) this.SBRNexusEnvironmentForOrganisation = data[index].value;
      });
    }
  }

  async CheckDocuments() {
    if (this._request?.documentInfo?.length > 0) {
      for (let doc of this._request.documentInfo) {
        // remove forbidden doc types based on workflow:
        if (!this.checkDocumenttypeAllowedInWorkflow(doc.documentType, doc.workflowPlaceholderId)) {
          let placeholder: WorkflowStep1Placeholder | undefined = this.shouldBeOnPlaceholderPosition(doc.documentType);
          if (placeholder == undefined) {
            doc.error = "DocumentTypeNotSupportedInWorkflow";

            // no placeholder claims this document, so the useful hint is everything the workflow accepts anywhere,
            // not the types of whichever slot it happened to be assigned to (it may be assigned to none at all)
            const supportedTypes = this.allSupportedDocumentTypes();

            if (supportedTypes.length > 0) {
              const keys = supportedTypes.map(s => `Request.Document.${s}`);
              if (!this.shownNotSupportedToast) {
                this.shownNotSupportedToast = true;
                const lead = this.translations.toastsUseOneOfTheFollowing ?? '';
                this.translateService.get(keys).subscribe((resMap: any) => {
                  const translated = keys.map(k => resMap[k] ?? '').filter(t => t).join(` ${this.translations.or} `);
                  const message = (lead ? lead + ' ' : '') + translated;
                  this.toastService.showError(this.translations.error, message);
                });
              }
            } else {
              if (!this.shownNotSupportedToast) {
                this.shownNotSupportedToast = true;
                this.toastService.showError(this.translations.error, this.translations.toastsDocumentTypeNotSupportedInWorkflow);
              }
            }

            if (doc.documentStatus == 'New') {
              this.deleteDocument(doc.id, false);
            }
          }
          else {
            let existingDocumentForPlaceholder = this._request.documentInfo.find(document => document.workflowPlaceholderId == placeholder!.placeholderId);

            if (existingDocumentForPlaceholder)
              this.updatePlaceholderId(existingDocumentForPlaceholder, null)

            this.updatePlaceholderId(doc, placeholder.placeholderId);
          }
        }

      }
    }
  }

  public deleteDocument(docId: string, showToast: boolean = true): void {
    let document = this._request.documentInfo.find(document => document.id === docId);
    if (!document) return;
    this.documentService.deleteDocument(this._request.id, docId)
      .then((value: any) => {
        lastValueFrom(value).then(() => {
          if (showToast) { this.toastService.showSuccess("" + this.translations.toastsDeleted + "", "" + this.translations.toastsRequestDocumentDeleted + "") };

        }).catch(() => {
        });
      }).catch(() => {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestDocumentDeleteError + "");
      });
  }

  public checkDocumenttypeAllowedInWorkflow(documentType: string, placeholderId?: number | null): boolean {
    if (!this.workflowData || !documentType || documentType.toLocaleLowerCase() === "unknown") {
      return true;
    } else if (this.workflowData
      && (!this.workflowData.step1.placeholders || (this.workflowData.step1.allowAddDocuments && placeholderId == null))
      && this.workflowData.step1.supportedDocumentTypes.includes(documentType)) {
      return true;
    }
    else if (this.workflowData
      && this.workflowData.step1.placeholders
      && this.workflowData.step1.placeholders.find(s => s.placeholderId === placeholderId)?.supportedDocumentTypes.includes(documentType)) {
      return true;
    }
    else if (this.workflowData && !this.workflowData.step1.allowRendering) {
      return true;
    }
    else {
      return false;
    }
  }

  /**
   * Every document type this workflow accepts anywhere: the union of all step1 placeholder types, plus the
   * loose step1 types when a document may be added outside a placeholder. Mirrors the branches of
   * checkDocumenttypeAllowedInWorkflow, so it answers "what could I have uploaded instead?" for a rejected document.
   */
  private allSupportedDocumentTypes(): string[] {
    const step1 = this.workflowData?.step1;
    if (!step1) return [];

    const types = new Set<string>();
    step1.placeholders?.forEach(placeholder => placeholder.supportedDocumentTypes?.forEach(type => { if (type) types.add(type); }));
    // without placeholders (or when loose documents are allowed) step1's own list is what applies
    if (!step1.placeholders?.length || step1.allowAddDocuments) {
      step1.supportedDocumentTypes?.forEach(type => { if (type) types.add(type); });
    }
    return [...types];
  }

  shouldBeOnPlaceholderPosition(documentType: string): WorkflowStep1Placeholder | undefined {
    let hasStep1Placeholders: boolean | undefined = this.workflowData?.step1.placeholders && this.workflowData.step1.placeholders.length > 0;

    if (hasStep1Placeholders) {
      let placeholderWithMatchingDocumentType = this.workflowData!.step1.placeholders!.find(placeholder => placeholder.supportedDocumentTypes.includes(documentType));
      let placeholderHasCorrectDocument = this._request.documentInfo.findIndex(document => document.workflowPlaceholderId == placeholderWithMatchingDocumentType?.placeholderId
        && placeholderWithMatchingDocumentType?.supportedDocumentTypes.includes(document.documentType)) > -1;

      if (!placeholderWithMatchingDocumentType || placeholderHasCorrectDocument) return;

      return placeholderWithMatchingDocumentType;
    }
    return;
  }

  updatePlaceholderId(document: BuilderDocumentModel, placeholderId: number | null) {
    document.workflowPlaceholderId = placeholderId;
    this.documentService.updateDocumentPlaceholderId(this._request.id, document.id, placeholderId ?? -1)
      .then((data: any) => {
        lastValueFrom(data)
          .catch(error => this.loggingService.logException(error))
      }).catch(error => this.loggingService.logException(error));
  }




  async addMyselfForSigning() {
    let myself = new DocumentActor();
    myself.firstname = this.user.firstname;
    myself.prefix = this.user.prefix ?? '';
    myself.lastname = this.user.lastname;
    myself.email = SetNormalizedEmail(this.user.email);
    myself.mobile = this.user.phoneNumber;
    myself.language = this.localStorageService.Language;
    myself.validateRealIdentity = false;
    myself.identityProvider = this.workflowData?.step2?.allowedIdentityProviders && this.workflowData?.step2?.allowedIdentityProviders.length > 0 ? this.workflowData?.step2?.allowedIdentityProviders[0] : IdentityProviderConstants.Default;
    myself.ordinal = 0;
    await this.addPersonCall(myself);
  }

  private async addPersonCall(person: DocumentActor) {
    (await this.actorApiClient.addDossierPerson(this._requestGuid, person)).subscribe({
      error: (error: HttpErrorResponse) => {
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsAddPersonError + "");
      }, next: () => {
        this.triedToAddMyself = false;
      }
    });
  }


  public async getRequest() {
    await this.requestBuilderApiClient.Get(this._requestGuid!).then(dossierResp => {
      lastValueFrom(dossierResp)
        .then(async (request: BuilderRequestModel) => {
          this._request = request;
          this.setRequestGuid(request.id);
          this.setRequest(request);
          if (this._request.workflowId) {
            this.workflowId = this._request.workflowId;
            this.setWorkflowId(this.workflowId);
            // also load the workflow data into the service so it can be used anywhere in the page!
            this.initializeForWorkflow(this.workflowId);
          }
          await this.setWorkflow();

          if (Object.keys(this._request).length !== 0) {
            // verify whether Builder is allowed for this request:
            if (this._request.status &&
              this._request.status.toLocaleLowerCase() != StatusEnum.New.toLowerCase() &&
              this._request.status.toLocaleLowerCase() != StatusEnum.Active.toLowerCase() &&
              this._request.status.toLocaleLowerCase() != StatusEnum.Error.toLowerCase() &&
              this._request.status.toLocaleLowerCase() != StatusEnum.Processing.toLowerCase()) {
              this.navigate.To_Status(this._requestGuid);
            }

            await this.CheckDocuments();
          }
        }).catch((error) => this.loggingService.logException(error));
    });

  }

  public updateRequest(event: any) {
    if (event?.dossierPersons == undefined) event.dossierPersons = [];
    this.setRequest(event);
    if (this._requestGuid == undefined || this._requestGuid == null) {
      this._requestGuid = event.id;
    }
  }

  public getRequestById(event: string) {
    if (this._requestGuid === null || this._requestGuid === undefined || this._requestGuid === "") {
      this.setRequestGuid(event);
    }
    this.getRequest().then().catch((error: any) => {
      this.loggingService.logException(error);
    });
  }

  public async changeOrderActors(actor: DocumentActor) {
    (await (this.actorApiClient.changeOrder(this._requestGuid, actor.documentId, actor.id, this._request.actors))).subscribe({
      error: (error: HttpErrorResponse) => { },
      next: (value: any) => {
      }
    });
  }

  public async Validate() {
    try {
      let sendReq = (await this.requestBuilderApiClient.Validate(this._requestGuid));
      let result = await lastValueFrom(sendReq);
      this.setValidationSummary(result);
    } catch (error: any) {
      // Validate is called 'fire and forget' from many places, a failing one may never break the caller
      this.loggingService.logException(error instanceof Error ? error : new Error(`Validate failed for request ${this._requestGuid}`));
    }
  }
  public async mergeSendgroups() {
    await (this.sendGroupApiClient.mergeSendgroups(this._requestGuid)).subscribe();
  }

  async SendRequest(requestGuid: string) {
    if (this.isSendingRequest) {
      // Already busy: a second Send would activate the workflow twice, the spinner on the button is the feedback
      return;
    }
    if (this.workflowData?.requireKVK && !this.organisationHasKVK) {
      if (this.toastService.toasts.findIndex(p => p.header === this.translations.KvKHeader) == -1)
        this.toastService.showError(this.translations.KvKHeader, this.translations.toastsNoChamberOfCommerceNumberError, 10000);
      return;
    }
    let currentUserIsActor = this._actors.findIndex(actor => MatchContacts(this.user, actor));
    let firstSendGroup = this._sendgroups.find(s => Math.min(s.order));
    let userInFirstSendgroup = null;
    if (currentUserIsActor > -1) {
      userInFirstSendgroup = firstSendGroup?.actions.find(s => s.personGuid === this._actors[currentUserIsActor].dossierPersonId);
      if (userInFirstSendgroup) {
        this.windowToOpenForIfActorInFirstSendGroup = window.open("about:blank", "_blank");
      }
    }
    this.setIsSendingRequest(true);
    try {
      let sendReq = (await this.requestBuilderApiClient.Send(requestGuid));
      let result = await lastValueFrom(sendReq.pipe(timeout(this.sendRequestTimeoutInMs)));
      if (result && (result.hasValidationErrors || !result.passedValidations)) {
        // NOT sent: the Api answers Ok with the validations that blocked the send
        this.closeWindowForFirstSendGroupActor();
        // load them into the frontend so the steps show WHAT is wrong, the toast tells the user it was not sent:
        this.setValidationSummary(result);
        this.logSendFailure(requestGuid, BuilderSendRefusalReasonEnum.ValidationsFailed, null);
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestSendValidationsFailed + "");
        return;
      }
    } catch (error: any) {
      this.closeWindowForFirstSendGroupActor();
      this.handleSendRequestFailure(requestGuid, error);
      return;
    } finally {
      this.setIsSendingRequest(false);
    }
    if (userInFirstSendgroup) {
      let url = this.router.serializeUrl(this.router.createUrlTree([this.navigate.Viewer_Url(requestGuid)]));
      if (Is_ANY_Download_Action(firstSendGroup?.actions[0].actionType)) {
        url = this.router.serializeUrl(this.router.createUrlTree([this.navigate.Download_Url(requestGuid)]));
      }
      this.windowToOpenForIfActorInFirstSendGroup!.location.href = url;
    }
    this.toastService.showSuccess(this.translations.toastsSent, this.translations.toastsRequestSent)
    this.navigate.To_Dashboard();
  }

  private setIsSendingRequest(isSending: boolean) {
    this.isSendingRequest = isSending;
    this.isSendingRequest$.next(isSending);
  }

  /**
   * Closes the (blank) tab that was opened for the user when he is an actor in the first sendgroup.
   * Needed on EVERY failure, else the user is left with an empty tab
   */
  private closeWindowForFirstSendGroupActor() {
    if (this.windowToOpenForIfActorInFirstSendGroup) {
      this.windowToOpenForIfActorInFirstSendGroup.close();
      this.windowToOpenForIfActorInFirstSendGroup = null;
    }
  }

  /**
   * Handles EVERY way a SEND can fail, so the user always learns WHY his request was not sent.
   * <para>Sending MOSTLY succeeds, so cases where we cannot know (timeout/unexpected) never claim it failed</para>
   * @param requestGuid the request that was not sent
   * @param error whatever the Api/rxjs threw
   */
  private handleSendRequestFailure(requestGuid: string, error: any) {
    // WE stopped waiting, the server can still be sending: never tell the user it failed
    if (error instanceof TimeoutError) {
      this.logSendFailure(requestGuid, "Timeout", error, true);
      this.toastService.showWarning("" + this.translations.warning + "", "" + this.translations.toastsRequestSendTakingLong + "");
      // reload: WHEN it was sent after all, the builder redirects to the status page
      this.reloadRequestAfterFailedSend();
      return;
    }

    let httpError: HttpErrorResponse | null = error instanceof HttpErrorResponse ? error : null;
    // the Api tells us WHY it refused (Conflict/PaymentRequired), older/unknown answers stay 'Unknown':
    let refusal: BuilderSendRefusedModel | null = httpError?.error?.reason ? httpError.error as BuilderSendRefusedModel : null;
    let reason: BuilderSendRefusalReasonEnum = refusal?.reason ?? BuilderSendRefusalReasonEnum.Unknown;

    switch (httpError?.status) {
      case HttpResponseCodesEnum.NoConnection:
        // the call never reached the server (offline/blocked/aborted)
        this.logSendFailure(requestGuid, "NoConnection", error, true);
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestSendNoConnection + "");
        return;
      case HttpResponseCodesEnum.GatewayTimeout:
        // the GATEWAY stopped waiting for the server, so just like our own timeout: it can still be sending
        this.logSendFailure(requestGuid, "GatewayTimeout", error, true);
        this.toastService.showWarning("" + this.translations.warning + "", "" + this.translations.toastsRequestSendTakingLong + "");
        this.reloadRequestAfterFailedSend();
        return;
      case HttpResponseCodesEnum.PaymentRequired:
        // Insufficient credits, the PaymentRequiredInterceptor already shows the (special) credits toast,
        // a second toast would only stack on top of it:
        this.logSendFailure(requestGuid, BuilderSendRefusalReasonEnum.InsufficientCredits, error);
        return;
      case HttpResponseCodesEnum.NotFound:
        // NotFound has ONE meaning: no access (or no longer existing), so there is nothing left to do here
        this.logSendFailure(requestGuid, BuilderSendRefusalReasonEnum.NoAccess, error);
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestSendNoAccess + "");
        this.navigate.To_Dashboard();
        return;
      case HttpResponseCodesEnum.Conflict:
        // the STATUS of the request does not allow sending (anymore)
        this.logSendFailure(requestGuid, reason, error);
        this.showStatusRefusalToast(reason);
        // reload: the status changed under water, the builder redirects when it can no longer be build
        this.reloadRequestAfterFailedSend();
        return;
      default:
        // Unexpected (500/unknown): activating is NOT transactional, so it might be (partially) sent
        this.logSendFailure(requestGuid, "Unexpected", error, true);
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestSendUnexpectedError + "");
        this.Validate();
        return;
    }
  }

  /**
   * Tells the user WHICH status blocked his send
   * @param reason reason the Api returned with the Conflict
   */
  private showStatusRefusalToast(reason: BuilderSendRefusalReasonEnum) {
    switch (reason) {
      case BuilderSendRefusalReasonEnum.RequestIsProcessing:
        // documents are still being converted/handled, sending works once that is done
        this.toastService.showWarning("" + this.translations.warning + "", "" + this.translations.toastsRequestSendProcessing + "");
        return;
      case BuilderSendRefusalReasonEnum.RequestAlreadySent:
        this.toastService.showWarning("" + this.translations.warning + "", "" + this.translations.toastsRequestSendAlreadySent + "");
        return;
      case BuilderSendRefusalReasonEnum.RequestNotReady:
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestSendNotReady + "");
        this.Validate();
        return;
      default:
        this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestSentError + "");
        return;
    }
  }

  /**
   * Reloads the request after a failed send.
   * getRequest redirects to the Status page when the request turns out to be sent after all
   */
  private reloadRequestAfterFailedSend() {
    this.getRequest().catch((error: any) => this.loggingService.logException(error));
  }

  /**
   * Logs to Application Insights WHY a send did not happen, so it can be found back per request
   * @param requestGuid the request that was not sent
   * @param reason refusal reason (Api) or our own label for a technical failure
   * @param error whatever the Api/rxjs threw
   * @param isUnexpected true for technical failures, those are ALSO logged as an exception
   */
  private logSendFailure(requestGuid: string, reason: string, error: any, isUnexpected: boolean = false) {
    let httpStatus: number | null = error instanceof HttpErrorResponse ? error.status : null;
    this.loggingService.logEvent("Builder-Send-Failed", {
      requestGuid: requestGuid,
      reason: reason,
      httpStatus: httpStatus,
      message: error?.message ?? null
    });
    if (isUnexpected) {
      this.loggingService.logException(new Error(`Builder Send failed for request ${requestGuid}: ${reason} (http status: ${httpStatus ?? "none"})`));
    }
  }

  public async createSendgroup(requestGuid: string, order: number, ForPersonGuids: string[]) {
    await (this.sendGroupApiClient.createSendgroup(requestGuid, order, ForPersonGuids)).subscribe(() => {
      this.Validate();
    });
  }

  public async assignToSendgroup(requestGuid: string, sendGroupGuid: string, ForPersonGuids: string[]) {
    await (this.sendGroupApiClient.assignSendgroup(requestGuid, sendGroupGuid, ForPersonGuids)).subscribe(() => {
      this.Validate();
    });
  }

  public async changeOrderActorsInSendGroup(actorsToUpdate: ChangeActionsInPersonSendGroupModel[], dossierPersonGuid: string) {
    (await (this.sendGroupApiClient.changeActorsByPersonOrder(this._requestGuid, dossierPersonGuid, actorsToUpdate))).subscribe({
      error: (error: HttpErrorResponse) => { },
      next: (value: any) => {
        this.Validate();
      }
    });
  }




  public getProviderValue(provider: Provider) {
    if (provider.parameters?.length > 0) {
      return provider.parameters[0];
    }
    else if (provider.signatureProviderType === SignatureProviderTypeEnum.TokenCertificateSignatureProvider) {
      return "Token";
    }
    return provider.signatureProviderType;
  }

  /**
   * verifies all RECIEVED signature providers and removes providers that the WORKFLOW forbids
   * @returns
   */
  private verifyAllowedSignatureProvidersForWorkflow() {
    // we need Providers to process:
    if (!this._allSignatureProviders) {
      return;
    }

    this._allowedSignatureProviders = [];
    this._allowedSignatureProviders = this._allowedSignatureProviders.concat(this._allSignatureProviders);
    // workflow can DISABLLOW Eseal, split it from the options:
    if (!this.allowESealSignatureProvider) {
      var eSealIndexOptions = this._allowedSignatureProviders.findIndex((qtsp: Provider) => qtsp.signatureProviderType === SignatureProviderTypeEnum.Eseal);
      if (eSealIndexOptions > 0) {
        this._allowedSignatureProviders.splice(eSealIndexOptions, 1);
      }
    }

    // emit:
    this.signatureProviders$.next(this._allowedSignatureProviders);
  }
  /**
   * Verifies allowed signature providers for ACTOR whether selected or not:
   * @param actorGuid
   * @returns
   */
  private getAvailableSignatureProviders(actorGuid: string) {
    // we need Providers to process:
    if (!this._allSignatureProviders) {
      return;
    }

    // setup default model:
    let availableSignatureProviders: SignatureProviderAvailability = new SignatureProviderAvailability;
    availableSignatureProviders.selectedSignatureProviders = [];
    availableSignatureProviders.disableSignatureProviders = false;

    //for what actor are we operating:
    let actor: BuilderActorModel = SingleOrDefault(this._actors, 'id', actorGuid)!;
    if (!actor) {
      return;
    }
    // only works for Sign Actors
    if (actor.action != ActorActionEnum.Sign) {
      return;
    }
    // are allowedQtsps already set?
    let allowedQtspsByActor: string[] = actor.allowedQtsps ?? [];
    // if so,  find token and rename:
    if (allowedQtspsByActor.length > 0) {
      allowedQtspsByActor?.forEach((qtsp, index) => {
        // has token been st, Rename:
        if (qtsp.toLocaleLowerCase() === "token") {
          allowedQtspsByActor[index] = SignatureProviderTypeEnum.TokenCertificateSignatureProvider;
        }
      });
    }

    //Default when the user allready has allowedQTSPs (for example with edit signaturefield) this is always selected.
    if (allowedQtspsByActor && allowedQtspsByActor.length > 0 && this._allowedSignatureProviders.length > 0) {
      this._allowedSignatureProviders.filter((provider: Provider) => {
        if (allowedQtspsByActor.some((qtsp: string) => provider.id.endsWith(qtsp)) && !availableSignatureProviders.selectedSignatureProviders.includes(provider)) {
          availableSignatureProviders.selectedSignatureProviders.push(provider);
          availableSignatureProviders.disableSignatureProviders = (this.allowedSignatureProvidersByPlaceholder.some(s => s.allowedQTSP?.length > 0) || this.allowedSignatureProvidersByWorkflow.length > 0);
        }
      });
    } else {
      // The behaviour of available signature providers is as follows and they overrule each other in the following order:
      //   If the person is an account user, only the configured signature provider from the user account is selected (disabled), but all other providers can be selected by the sender (everything can be selected).
      if (actor.isCustomer && this.allowedSignatureProvidersByWorkflow.length === 0 && this.allowedSignatureProvidersByPlaceholder.filter(ph => ph.allowedQTSP.length > 0).length === 0) {
        if (actor.preferredSignatureProvider) {
          if (actor.preferredSignatureProvider === SignatureProviderTypeEnum.CscSignatureProvider) {
            let cscProvidersWithParams = this._allowedSignatureProviders.filter((provider: Provider) => provider.parameters != null);
            let indexOfCSCProvider = cscProvidersWithParams.findIndex((provider: Provider) => provider.parameters.includes(actor.preferredCscProvider!))
            if (indexOfCSCProvider > -1) {
              availableSignatureProviders.selectedSignatureProviders.push(cscProvidersWithParams[indexOfCSCProvider]);
            }
          }
        } else if (!actor.preferredSignatureProvider && this._allowedSignatureProviders?.some((qtsp: Provider) => qtsp.signatureProviderType === SignatureProviderTypeEnum.Eseal)) {
          availableSignatureProviders.selectedSignatureProviders.push(SingleOrDefault(this._allowedSignatureProviders, "signatureProviderType", SignatureProviderTypeEnum.Eseal)!);
        }
      } else {
        //  If the workflow config only allows one or more specific providers, they are selected and disabled and the other providers are all disabled (nothing can be (un)selected).
        if (this.allowedSignatureProvidersByWorkflow.length > 0) {
          this._allowedSignatureProviders.filter((provider: Provider) => {
            if (this.allowedSignatureProvidersByWorkflow?.some((qtsp: string) => provider.id.includes(qtsp)) && !availableSignatureProviders.selectedSignatureProviders.includes(provider)) {
              availableSignatureProviders.selectedSignatureProviders.push(provider);
              availableSignatureProviders.disableSignatureProviders = true;
            }
          });
        } else if (this.allowedSignatureProvidersByPlaceholder?.filter(placeholder => placeholder.allowedQTSP?.length > 0)?.length > 0) {
          this._allowedSignatureProviders.filter((provider: Provider) => {
            if (this.allowedSignatureProvidersByPlaceholder.some((qtsp: any) => qtsp.placeholderId == actor.workflowPlaceholderId && provider.id.includes(qtsp.allowedQTSP)) && !availableSignatureProviders.selectedSignatureProviders.includes(provider)) {
              availableSignatureProviders.selectedSignatureProviders.push(provider);
              availableSignatureProviders.disableSignatureProviders = true;
            }
          });
        } else {
          //   If the user is not an account user, only PKIsigning is selected as signature provider, but all other providers can be selected by the sender (everything can be selected).
          availableSignatureProviders.selectedSignatureProviders.push(SingleOrDefault(this._allowedSignatureProviders, "signatureProviderType", SignatureProviderTypeEnum.Eseal)!);
        }
      }
    }
    let actorIndex = this._actors.findIndex(s => s.id === actor.id);
    // workflow can DISABLLOW Eseal, split it from the SELECTED options:
    if (!this.allowESealSignatureProvider) {
      var eSealIndexSelected = availableSignatureProviders.selectedSignatureProviders.findIndex((qtsp: Provider) => qtsp.signatureProviderType === SignatureProviderTypeEnum.Eseal);
      if (eSealIndexSelected > 0) {
        availableSignatureProviders.selectedSignatureProviders.splice(eSealIndexSelected, 1);
      }
    }


    availableSignatureProviders.selectedSignatureProviders.forEach(qtsp => {
      if (!this._actors[actorIndex].allowedQtsps) {
        this._actors[actorIndex].allowedQtsps = [];
      }

      this._actors[actorIndex].allowedQtsps.push(this.getProviderValue(qtsp));
      this._actors[actorIndex].disableQtsps = availableSignatureProviders.disableSignatureProviders;
    });
    this._actors[actorIndex].allowedQtsps = [...new Set(this._actors[actorIndex].allowedQtsps)]
  }

  private async getSignatureProviders() {
    this.engineService.getSignatureProviders().then(providers => {
      this._allowedSignatureProviders = providers;
      this.signatureProviders$.next(this._allowedSignatureProviders);
    }).catch(error => {
      this.loggingService.logException(error);
    });
  }

  public applyAction(action: RequestBuilderActionsEnum) {
    switch (action) {
      case RequestBuilderActionsEnum.EditRequest:
        this.modalOpenerService.openNewRequestPickerModal(this._request, false);
        break;
      case RequestBuilderActionsEnum.Purge:
        this.purgeRequest(this._requestGuid);
        break;
      default:
        break;
    }
  }

  public isLatestTab(): boolean {
    return this.currentTab === this.workflowSteps[this.workflowSteps.length - 1];
  }

  public isGotoRequestDisabled(): boolean {
    return (
      // for most workflows validations are leading:
      (!this._validationSummary || !this._validationSummary.passedValidations)
    );
  }

  private purgeRequest(requestId: string) {
    this.requestApiClient.purgeRequest(requestId)
      .then(value => {
        lastValueFrom(value)
          .then(() => {
            this.toastService.showSuccess(this.translations.toastsPurged, this.translations.toastsRequestPurged);
            this.navigate.To_Dashboard();
          })
          .catch(() => this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestsPurgeError + ""));
      })
      .catch(() => this.toastService.showError("" + this.translations.error + "", "" + this.translations.toastsRequestsPurgeError + ""));
  }
}

